import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';


type ScaleKey = 0 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
type Scale = Record<ScaleKey, string>;

const darkSurface: Scale = {
  0: '#ffffff',
  50: '#f2f1ee',
  100: '#dedcd7',
  200: '#b9b7b1',
  300: '#94928c',
  400: '#706e69',
  500: '#504e49',
  600: '#3a3936',
  700: '#101013',
  800: '#0f0f12',
  900: '#0d0d0f',
  950: '#08080a',
};

const primary: Scale = {
  50: '#fdf8f0',
  100: '#fbeed9',
  200: '#f4dab0',
  300: '#edc487',
  400: '#e9b975',
  500: '#e8b064',
  600: '#d89a45',
  700: '#b67d34',
  800: '#8f612a',
  900: '#6c4a22',
  950: '#3c2812',
  0: ''
};

const MIRROR_PAIRS: [ScaleKey, ScaleKey][] = [
  [0, 950],
  [50, 900],
  [100, 800],
  [200, 700],
  [300, 600],
  [400, 500],
];

function mirror(scale: Scale): Scale {
  const out = { ...scale };
  for (const [a, b] of MIRROR_PAIRS) {
    out[a] = scale[b];
    out[b] = scale[a];
  }
  return out;
}

const lightSurface: Scale = mirror(darkSurface);

function alpha(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function semanticColorScheme(surface: Scale) {
  return {
    surface,
    primary: {
      color: primary[500],
      inverseColor: '#14100a',
      hoverColor: primary[400],
      activeColor: primary[600],
    },
    content: {
      background: surface[900],
      borderColor: alpha(surface[0], 0.07),
      color: surface[50],
    },
    overlay: {
      modal: {
        background: surface[900],
        borderColor: alpha(surface[0], 0.07),
        color: surface[50],
      },
      popover: {
        background: surface[800],
        borderColor: alpha(surface[0], 0.07),
        color: surface[50],
      },
    },
    formField: {
      background: alpha(surface[0], 0.05),
      borderColor: alpha(surface[0], 0.12),
      color: surface[50],
      placeholderColor: alpha(surface[0], 0.28),
      hoverBorderColor: alpha(surface[0], 0.2),
      focusBorderColor: primary[500],
      focusRing: {
        color: alpha(primary[500], 0.25),
        width: '0',
        style: 'none',
        offset: '0',
      },
      paddingX: '11px',
      paddingY: '7px',
    },
    text: {
      color: surface[50],
      mutedColor: alpha(surface[0], 0.5),
    },
    mask: {
      background: 'rgba(0, 0, 0, 0.6)',
      color: surface[50],
    },
  };
}

function buttonColorScheme(surface: Scale) {
  return {
    root: {
      primary: {
        background: primary[500],
        hoverBackground: primary[400],
        activeBackground: primary[600],
        borderColor: primary[500],
        hoverBorderColor: primary[400],
        activeBorderColor: primary[600],
        color: '#14100a',
        hoverColor: '#14100a',
        activeColor: '#14100a',
        focusRing: { color: alpha(primary[500], 0.25) },
      },
      secondary: {
        background: 'transparent',
        hoverBackground: alpha(surface[0], 0.05),
        activeBackground: alpha(surface[0], 0.08),
        borderColor: alpha(surface[0], 0.12),
        hoverBorderColor: alpha(surface[0], 0.2),
        activeBorderColor: alpha(surface[0], 0.25),
        color: alpha(surface[0], 0.75),
        hoverColor: surface[50],
        activeColor: surface[50],
      },
    },
  };
}

function selectScheme(surface: Scale) {
  return {
    root: {
      background: alpha(surface[0], 0.04),
      borderColor: alpha(surface[0], 0.1),
      color: surface[50],
      hoverBorderColor: alpha(surface[0], 0.2),
      focusBorderColor: primary[500],
    },
    overlay: {
      background: surface[800],
      borderColor: alpha(surface[0], 0.08),
      color: surface[50],
    },
    option: {
      color: alpha(surface[0], 0.75),
      focusBackground: alpha(surface[0], 0.05),
      selectedBackground: alpha(primary[500], 0.12),
      selectedColor: primary[500],
    },
  };
}

export const FynitePreset = definePreset(Aura, {
  semantic: {
    primary,
    colorScheme: {
      dark: semanticColorScheme(darkSurface),
      light: semanticColorScheme(lightSurface),
    },
  },

  components: {
    dialog: {
      root: {
        borderRadius: '14px',
        shadow: '0 30px 80px rgba(0, 0, 0, 0.6)',
      },
      header: { padding: '22px 22px 16px 22px', gap: '8px' },
      title: { fontSize: '15px', fontWeight: '500' },
      content: { padding: '0' },
      footer: { padding: '16px 22px 22px 22px', gap: '9px' },
    },

    inputtext: {
      root: {
        paddingX: '11px',
        paddingY: '7px',
      },
    },

    button: {
      root: {
        borderRadius: '9px',
        paddingX: '14px',
        paddingY: '8px',
        label: { fontWeight: '500' },
      },
      colorScheme: {
        light: buttonColorScheme(lightSurface),
        dark: buttonColorScheme(darkSurface),
      },
    },

    select: {
      colorScheme: {
        light: selectScheme(lightSurface),
        dark: selectScheme(darkSurface),
      },
    },
  },

  extend: {
    fynite: {
      success: '#6fcf97',
      successText: '#8fe0b0',
      info: '#9fc3ff',
      infoStrong: '#7fb2ff',
      warning: primary[500],
      danger: '#ef5b5b',
      onPrimary: '#14100a',
    },
  },
});